import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useUpdateAdminMutation } from "../slices/AdminSilce";
import { getCurrentDate } from "../utils/getCurrentDate";
import { toast } from "react-toastify";
import FieldWarning from "../components/FieldWarning";
import AccessListEditor from "../components/AccessListEditor";
import { effectiveAccess } from "../utils/adminAccess";

const EditAdminDetailsScreen = ({ adminDet, editable }) => {
  const { userInfo } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [adminName, setAdminName] = useState(adminDet?.adminName);
  // Owners' access is fixed and nobody can change it; only an Admin's
  // access list is editable (by owners — the server enforces this).
  const targetIsOwner = adminDet?.adminType === "Owner";
  // Owners show their fixed server-side access, Admins their stored list
  const [access, setAccess] = useState(effectiveAccess(adminDet));

  const [address, setAddress] = useState(adminDet?.address);
  const [contactNumber, setContactNumber] = useState(adminDet?.mobileNumber);
  const [pincode, setPincode] = useState(adminDet?.pincode);
  const [email, setEmail] = useState(adminDet?.email);
  const [aadharNumber, setAadharNumber] = useState(adminDet?.aadharNumber);
  const [panNumber, setPanNumber] = useState(adminDet?.panNumber);

  const [workShift, setWorkShift] = useState(adminDet?.workShift);
  const [gender, setGender] = useState(adminDet?.gender);

  const [outletIdPresent, setOutletIdPresent] = useState(false);
  // const [companyIdPresent, setCompanyIdPresent] = useState(false);
  const [namePresent, setNamePresent] = useState(false);
  const [genderPresent, setGenderPresent] = useState(false);
  const [recentOtpPresent, setRecentOtpPresent] = useState(false);
  const [emailPresent, setEmailPresent] = useState(false);
  const [aadharNumberPresent, setAadharNumberPresent] = useState(false);
  const [panNumberPresent, setPanNumberPresent] = useState(false);
  const [joinedDatePresent, setJoinedDatePresent] = useState(false);
  const [dateOfBirthPresent, setDateOfBirthPresent] = useState(false);
  const [addressPresent, setAddressPresent] = useState(false);
  const [pincodePresent, setPincodePresent] = useState(false);
  const [contactNumberPresent, setContactNumberPresent] = useState(false);

  const workShifts = [1, 2, 3, 4];
  const availableGender = ["Male", "Female", "Other"];

  const [editAdmin, { isLoading }] = useUpdateAdminMutation();

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toISOString().split("T")[0]; // Converts to 'YYYY-MM-DD'
  };

  const [dateOfBirth, setDateOfBirth] = useState(
    formatDate(adminDet?.dateOfBirth)
  );
  const [joinedDate, setJoinedDate] = useState(
    formatDate(adminDet?.joinedDate)
  );

  useEffect(() => {
    if (adminName?.trim().length > 0) {
      setNamePresent(false);
    }

    if (gender?.length > 1) {
      setGenderPresent(false);
    }

    if (contactNumber?.length === 10 && contactNumber > 1) {
      setContactNumberPresent(false);
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailRegex.test(email) || email?.length === 0) {
      setEmailPresent(false);
    }

    if (aadharNumber?.length === 12 || aadharNumber?.length === 0) {
      setAadharNumberPresent(false);
    }

    if (panNumber?.length > 9 || panNumber?.length === 0) {
      setPanNumberPresent(false);
    }

    if (address?.length > 10 || address?.length === 0) {
      setAddressPresent(false);
    }

    if (pincode?.length === 6 || pincode?.length === 0) {
      setPincodePresent(false);
    }

    if (dateOfBirth < getCurrentDate()) {
      setDateOfBirthPresent(false);
    }

    if (joinedDate <= getCurrentDate()) {
      setJoinedDatePresent(false);
    }

    // if (companyId?.length === 4) {
    //   setCompanyIdPresent(false);
    // }
  }, [
    adminName,
    gender,
    email,
    aadharNumber,
    dateOfBirth,
    joinedDate,
    address,
    contactNumber,
    pincode,
    panNumber,
  ]);

  const verifyInputFeilds = async () => {
    let fieldValidationSuccess = true;

    if (adminName?.trim().length < 1 || adminName?.length > 100) {
      setNamePresent(true);
      fieldValidationSuccess = false;
    }

    if (gender?.length < 1) {
      setGenderPresent(true);
      fieldValidationSuccess = false;
    }

    if (!contactNumber || contactNumber?.length !== 10) {
      setContactNumberPresent(true);
      fieldValidationSuccess = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email)
      if (!emailRegex.test(email) || email?.length > 300) {
        setEmailPresent(true);
        fieldValidationSuccess = false;
      }
    if (aadharNumber)
      if (aadharNumber?.length !== 12 || Number(aadharNumber) < 0) {
        setAadharNumberPresent(true);
        fieldValidationSuccess = false;
      }
    if (panNumber)
      if (panNumber?.length < 8 || panNumber < 1) {
        setPanNumberPresent(true);
        fieldValidationSuccess = false;
      }

    if (address)
      if (address?.length < 10 || address?.length > 1000) {
        setAddressPresent(true);
        fieldValidationSuccess = false;
      }
    if (pincode)
      if (pincode?.length !== 6 || pincode < 1) {
        setPincodePresent(true);
        fieldValidationSuccess = false;
      }

    if (!dateOfBirth || dateOfBirth >= getCurrentDate()) {
      setDateOfBirthPresent(true);
      fieldValidationSuccess = false;
    }

    if (joinedDate > getCurrentDate()) {
      setJoinedDatePresent(true);
      fieldValidationSuccess = false;
    }

    // if (companyId?.length !== 4) {
    //   setCompanyIdPresent(true);
    //   fieldValidationSuccess = false;
    // }

    if (!fieldValidationSuccess)
      toast.error(
        "Some of the fields have invalid inputs. Please provide valid details"
      );
    return fieldValidationSuccess;
  };

  const handleSubmit = async () => {
    try {
      const data = {
        _id: adminDet?._id,
        adminName: adminName,
        ...(!targetIsOwner && { access: access }),
        gender: gender,
        mobileNumber: contactNumber,
        email: email,
        aadharNumber: aadharNumber,
        panNumber: panNumber,
        joinedDate: joinedDate,
        dateOfBirth: dateOfBirth,
        address: address,
        pincode: pincode,
        workShift: workShift,
        token: userInfo.token,
      };
      await editAdmin(data).unwrap();
      toast.success("Details Updated Successfully");
      editable(false);
    } catch (e) {
      toast.error(e?.data?.error || e?.error);
    }
  };

  const handleCancel = () => {
    editable(false);
  };

  return (
    <>
      <h6>Edit Admin Details</h6>
      <Row>
        <Col md={6}>
          <Form.Group as={Row} className="my-2" controlId="firstName">
            <Form.Label column="true" sm="3">
              Admin Name <label style={{ color: "red" }}>*</label> :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                placeholder="enter Name"
              ></Form.Control>{" "}
              {namePresent ? (
                <FieldWarning text="First name should have minimum 1 char and max 100 char" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>

          {/* <Form.Group as={Row} className="my-2" controlId="aadharNumber">
                  <Form.Label column="true" sm="3">
                    Company Id <label style={{ color: "red" }}>*</label>:
                  </Form.Label>
                  <Col sm="6">
                    <Form.Control
                      type="text"
                      pattern="[0-9]*" // Allow only digits
                      value={companyId}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                        setCompanyId(value);
                      }}
                      placeholder="enter company id"
                    />
                    {companyIdPresent ? (
                      <FieldWarning text="Company Id should be 4 characters long" />
                    ) : (
                      ""
                    )}
                  </Col>
                </Form.Group> */}
          <Form.Group as={Row} className="my-2" controlId="adminType">
            <Form.Label column="true" sm="3">
              Admin Type :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={adminDet?.adminType || ""}
                disabled
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="my-2" controlId="contactNumber">
            <Form.Label column="true" sm="3">
              Contact Number <label style={{ color: "red" }}>*</label> :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                pattern="[0-9]*"
                value={contactNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                  setContactNumber(value);
                }}
                placeholder="enter contact number"
              ></Form.Control>
              {contactNumberPresent ? (
                <FieldWarning text="Please provide 10 digit mobile number" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="gender">
            <Form.Label column="true" sm="3">
              Select Gender <label style={{ color: "red" }}>*</label> :
            </Form.Label>
            <Col sm="6">
              <Form.Select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                placeholder="Select Gender"
              >
                {availableGender.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
              {genderPresent ? (
                <FieldWarning text="Please select gender" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="workShift">
            <Form.Label column="true" sm="3">
              Work Shift:
            </Form.Label>
            <Col sm="6">
              <Form.Select
                value={workShift}
                onChange={(e) => setWorkShift(e.target.value)}
                placeholder="Select work Shift "
              >
                {workShifts.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group as={Row} className="my-2" controlId="dateOfBirth">
            <Form.Label column="true" sm="3">
              Date Of Birth <label style={{ color: "red" }}>*</label>:
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                placeholder="in format 'YYYY-MM-DD'"
              ></Form.Control>{" "}
              {dateOfBirthPresent ? (
                <FieldWarning text="Please provide valid date of birth" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="joinedDate">
            <Form.Label column="true" sm="3">
              Joined Date <label style={{ color: "red" }}>*</label>:
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="date"
                value={joinedDate}
                onChange={(e) => setJoinedDate(e.target.value)}
                placeholder="in format 'YYYY-MM-DD'"
              ></Form.Control>
              {joinedDatePresent ? (
                <FieldWarning text="Joined date can not be future date" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="email">
            <Form.Label column="true" sm="3">
              Email :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="enter your email"
              ></Form.Control>
              {emailPresent ? (
                <FieldWarning text="Please provide valid e-mail" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="my-2" controlId="address">
            <Form.Label column="true" sm="3">
              Address :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="enter  Address"
              ></Form.Control>
              {addressPresent ? (
                <FieldWarning text="Address should be atleast 10 chars long" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="pincode">
            <Form.Label column="true" sm="3">
              Pincode :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                pattern="[0-9]*" // Allow only digits
                value={pincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                  setPincode(value);
                }}
                placeholder="enter pincode"
              />
              {pincodePresent ? (
                <FieldWarning text="Please provide valid 6 digit Pincode" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="panNumber">
            <Form.Label column="true" sm="3">
              PAN Number :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value)}
                placeholder="enter PAN Number"
              ></Form.Control>
              {panNumberPresent ? (
                <FieldWarning text="Please provide valid PAN number" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="aadharNumber">
            <Form.Label column="true" sm="3">
              Aadhar Number :
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                pattern="[0-9]*" // Allow only digits
                value={aadharNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                  setAadharNumber(value);
                }}
                placeholder="enter Aadhar number"
              />
              {aadharNumberPresent ? (
                <FieldWarning text="Please provide 12 digit Aadhar number" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
        </Col>
        <Col md={12}>
          <h6 className="mt-3">Access</h6>
          {targetIsOwner && (
            <p>Owners' access is fixed and can't be edited by anyone.</p>
          )}
          <AccessListEditor
            access={access}
            onChange={setAccess}
            readOnly={targetIsOwner}
          />
        </Col>
        <Row
          sm="4"
          style={{
            alignItems: "center",
            justifyContent: "center",
            margin: "2rem 0rem",
          }}
        >
          <Col>
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              style={{ maxWidth: "7rem", margin: "1rem" }}
              onClick={handleSubmit}
            >
              {" "}
              Submit
            </Button>

            <Button
              variant="secondary"
              style={{ maxWidth: "7rem" }}
              onClick={handleCancel}
            >
              {" "}
              Cancel
            </Button>
          </Col>
        </Row>
      </Row>
    </>
  );
};

export default EditAdminDetailsScreen;
